import React, { useMemo } from "react";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";
import { useParams } from "react-router-dom";

import RealTimeCard from "./RealTimeCard";
import { trailingAverage, useRealtimeSeries } from "./useRealtimeSeries";
import { baseChartOption, lineSeries, metricColor } from "../charts/chartTheme";
import { EM_DASH, describe, formatNumber } from "../../utils/format";

const SkinTemperatureRealTime = () => {
  const { watchId } = useParams();

  const paths = useMemo(
    () => ({
      data: `/Health/${watchId}/realTime/skinTemperature`,
      status: `/Health/${watchId}/realTime/skinTemperatureStatusDescription`,
      progress: `/Health/${watchId}/realTime/skinTemperatureProgress`,
    }),
    [watchId]
  );

  const { values, samples, status, progress, min, max } = useRealtimeSeries(
    watchId,
    paths
  );

  const stats = useMemo(() => describe(values, 2), [values]);

  const buildOption = () =>
    baseChartOption({
      categories: samples,
      series: [
        lineSeries({
          name: "Skin temperature",
          data: values,
          color: metricColor("skinTemperature"),
          smooth: 0.2,
          showSymbol: false,
        }),
      ],
      xName: "Sample",
      yName: "°C",
      yMin: min !== null ? Math.floor(min - 1) : undefined,
      yMax: max !== null ? Math.ceil(max + 1) : undefined,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `Sample ${point.axisValue}<br/><strong>${formatNumber(
          point.value,
          2
        )} °C</strong>`;
      },
    });

  return (
    <RealTimeCard
      title="Live skin temperature"
      icon={<DeviceThermostatOutlinedIcon />}
      accent={metricColor("skinTemperature")}
      exportName="skinTemperature"
      status={status}
      progress={progress}
      hasData={values.length > 0}
      waitingCopy="Keep the watch in contact with the skin. Temperature samples appear here as they stream in."
      deps={[values]}
      buildOption={buildOption}
      stats={[
        {
          label: "Latest",
          value: values.length ? formatNumber(values[values.length - 1], 2) : EM_DASH,
          unit: "°C",
          meta: "Most recent sample",
        },
        {
          label: "Avg (last 30s)",
          value: trailingAverage(values, 30, 2),
          unit: "°C",
          meta: "Rolling window",
        },
        { label: "Range", value: stats.range, unit: "°C", meta: "This capture" },
        { label: "Samples", value: values.length || EM_DASH, meta: "Received so far" },
      ]}
    />
  );
};

export default SkinTemperatureRealTime;
